import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import EmployeeNavbar from "../Layout/EmployeeNavbar";
import API_BASE_URL from "../config";

/* ─── Inline styles (no extra CSS file needed) ─── */
const styles = {
  root: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    background: "#F4F6F9",
    minHeight: "100vh",
    overflow: "hidden",
  },
  pageWrapper: {
    padding: "120px 20px 20px",   // ✅ top right bottom left
    width: "100%",
    maxWidth: "100%",
    margin: 0,
    height: "calc(100vh - 20px)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  /* ── Header ── */
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: "28px",
  },
  pageTitle: {
    fontSize: "1.6rem",
    fontWeight: "700",
    color: "#1A202C",
    margin: 0,
    letterSpacing: "-0.3px",
  },
  pageSubtitle: {
    fontSize: "0.8rem",
    color: "#718096",
    marginTop: "2px",
  },
  newBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 20px",
    background: "#0052CC",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "0.82rem",
    fontWeight: "600",
    cursor: "pointer",
    letterSpacing: "0.3px",
    transition: "background 0.18s",
  },

  /* ── Filter Card ── */
  filterCard: {
    background: "#fff",
    borderRadius: "12px",
    padding: "18px 24px",
    marginBottom: "20px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    alignItems: "flex-end",
  },
  filterGroup: { display: "flex", flexDirection: "column", gap: "4px", minWidth: "150px" },
  filterLabel: { fontSize: "0.7rem", fontWeight: "600", color: "#718096", textTransform: "uppercase", letterSpacing: "0.5px" },
  filterSelect: {
    padding: "7px 10px",
    border: "1.5px solid #E2E8F0",
    borderRadius: "7px",
    fontSize: "0.82rem",
    color: "#2D3748",
    background: "#FAFAFA",
    outline: "none",
    cursor: "pointer",
  },
  filterInput: {
    padding: "7px 10px",
    border: "1.5px solid #E2E8F0",
    borderRadius: "7px",
    fontSize: "0.82rem",
    color: "#2D3748",
    background: "#FAFAFA",
    outline: "none",
  },
  clearBtn: {
    padding: "7px 16px",
    background: "transparent",
    border: "1.5px solid #CBD5E0",
    borderRadius: "7px",
    fontSize: "0.8rem",
    color: "#718096",
    cursor: "pointer",
    fontWeight: "500",
    alignSelf: "flex-end",
    transition: "all 0.15s",
  },

  /* ── Table Card ── */
  tableCard: {
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
    overflow: "hidden",
    flex: 1,                 // take remaining height
    display: "flex",
    flexDirection: "column",
  },
  tableWrapper: {
    width: "100%",
    flex: 1,              // ✅ take full remaining height
    overflowY: "auto",
    overflowX: "auto",
  },
  table: {
    width: "100%",
    minWidth: "900px",
    borderCollapse: "collapse",
    fontSize: "0.83rem",
  },
  thead: {
    position: "sticky",
    top: 0,
    zIndex: 5,
  },
  th: {
    background: "#F7F8FA",
    position: "sticky",
    top: 0,
    zIndex: 5,
    padding: "11px 14px",
    textAlign: "left",
    fontSize: "0.72rem",
    fontWeight: "700",
    color: "#718096",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "1px solid #E2E8F0",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "11px 14px",
    borderBottom: "1px solid #F0F2F5",
    color: "#2D3748",
    verticalAlign: "middle",
  },
  trHover: { transition: "background 0.12s" },

  /* ── Status Badge ── */
  badge: (status) => {
    const map = {
      APPROVED: { bg: "#ECFDF5", color: "#065F46", border: "#A7F3D0" },
      REJECTED: { bg: "#FEF2F2", color: "#991B1B", border: "#FECACA" },
      PENDING: { bg: "#FFFBEB", color: "#92400E", border: "#FDE68A" },
      CANCELLED: { bg: "#F3F4F6", color: "#4B5563", border: "#D1D5DB" },
      FORWARDED: { bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE" },
    };
    const s = map[status] || { bg: "#F3F4F6", color: "#374151", border: "#D1D5DB" };
    return {
      display: "inline-flex",
      alignItems: "center",
      gap: "5px",
      padding: "3px 10px",
      borderRadius: "20px",
      fontSize: "0.72rem",
      fontWeight: "600",
      background: s.bg,
      color: s.color,
      border: `1px solid ${s.border}`,
      letterSpacing: "0.3px",
    };
  },
  dot: (status) => {
    const colors = { APPROVED: "#10B981", REJECTED: "#EF4444", PENDING: "#F59E0B", CANCELLED: "#9CA3AF", FORWARDED: "#3B82F6" };
    return { width: 6, height: 6, borderRadius: "50%", background: colors[status] || "#9CA3AF", display: "inline-block" };
  },

  /* ── Action links ── */
  actionLink: (color = "#3B82F6") => ({
    color,
    cursor: "pointer",
    fontSize: "0.78rem",
    fontWeight: "600",
    padding: "3px 8px",
    borderRadius: "5px",
    border: `1px solid ${color}22`,
    background: `${color}0D`,
    transition: "all 0.15s",
    display: "inline-block",
  }),
  actionLinkDisabled: {
    color: "#CBD5E0",
    cursor: "not-allowed",
    fontSize: "0.78rem",
    fontWeight: "600",
    padding: "3px 8px",
    borderRadius: "5px",
    border: "1px solid #E2E8F0",
    background: "#F7F8FA",
    display: "inline-block",
  },

  /* ── Modal ── */
  backdrop: {
    position: "fixed", inset: 0,
    background: "rgba(15,20,35,0.45)",
    zIndex: 1050,
    display: "flex", alignItems: "center", justifyContent: "center",
    backdropFilter: "blur(3px)",
  },
  modalBox: {
    background: "#fff",
    borderRadius: "14px",
    width: "100%",
    maxWidth: "460px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
    overflow: "hidden",
    animation: "slideUp 0.22s ease",
  },
  modalHeader: {
    padding: "18px 24px 16px",
    borderBottom: "1px solid #F0F2F5",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalTitle: { fontSize: "1rem", fontWeight: "700", color: "#1A202C", margin: 0 },
  modalBody: { padding: "20px 24px" },
  modalFooter: {
    padding: "14px 24px",
    borderTop: "1px solid #F0F2F5",
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
  },
  formLabel: { fontSize: "0.75rem", fontWeight: "600", color: "#718096", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "5px", display: "block" },
  formControl: {
    width: "100%",
    padding: "9px 12px",
    border: "1.5px solid #E2E8F0",
    borderRadius: "8px",
    fontSize: "0.85rem",
    color: "#2D3748",
    background: "#FAFAFA",
    outline: "none",
    boxSizing: "border-box",
  },
  btnPrimary: {
    padding: "9px 22px",
    background: "#2D3748",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "0.83rem",
    fontWeight: "600",
    cursor: "pointer",
  },
  btnSecondary: {
    padding: "9px 22px",
    background: "transparent",
    color: "#718096",
    border: "1.5px solid #E2E8F0",
    borderRadius: "8px",
    fontSize: "0.83rem",
    fontWeight: "600",
    cursor: "pointer",
  },
  btnDanger: {
    padding: "9px 22px",
    background: "#EF4444",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "0.83rem",
    fontWeight: "600",
    cursor: "pointer",
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: "1.2rem",
    color: "#9CA3AF",
    cursor: "pointer",
    lineHeight: 1,
    padding: "2px",
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#A0AEC0",
  },
  sortIcon: { marginLeft: "4px", opacity: 0.5, fontSize: "0.7rem" },
};

/* Inject keyframe once */
if (typeof document !== "undefined" && !document.getElementById("myLeaves-keyframes")) {
  const s = document.createElement("style");
  s.id = "myLeaves-keyframes";
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
    @keyframes slideUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
    .ml-tr:hover td { background: #F7F9FC !important; }
    .ml-newbtn:hover { background: #1A202C !important; }
    .ml-clearbtn:hover { background: #F7F8FA; color: #4A5568; }
    .ml-action-edit:hover { background: #3B82F611 !important; }
    .ml-action-cancel:hover { background: #EF444411 !important; }
  `;
  document.head.appendChild(s);
}

/* ─────────────── Component ─────────────── */
const MyLeaves = () => {
  const [leaves, setLeaves] = useState([]);
  const [filteredLeaves, setFilteredLeaves] = useState([]);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [editingLeave, setEditingLeave] = useState(null);
  const [editForm, setEditForm] = useState({ leaveType: "", startDate: "", endDate: "", reason: "", document: null });
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [financialYear, setFinancialYear] = useState("");
  const [leaveSummary, setLeaveSummary] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const navigate = useNavigate();

  /* ── Auth guard ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!roles.includes("ROLE_USER")) { toast.error("Not authorized."); navigate("/"); }
  }, [navigate]);

  /* ── Company financial year ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${API_BASE_URL}/api/company-info`, { headers: { Authorization: "Bearer " + token } })
      .then(r => r.json())
      .then(data => {
        const company = Array.isArray(data) ? data[0] : data;
        if (company?.financialYear) setFinancialYear(company.financialYear);
      })
      .catch(console.error);
  }, []);

  /* ── Leave types ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/"); return; }
    axios.get(`${API_BASE_URL}/api/leave-types/all`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setLeaveTypes(r.data))
      .catch(err => { if ([401, 403].includes(err.response?.status)) { localStorage.removeItem("token"); navigate("/"); } });
  }, [navigate]);

  /* ── Fetch on financialYear ready ── */
  useEffect(() => {
    if (financialYear) { fetchLeaves(); fetchLeaveSummary(); }
  }, [financialYear]);

  const fetchLeaveSummary = async () => {
    const token = localStorage.getItem("token");
    const employeeId = localStorage.getItem("employeeId");
    try {
      const res = await axios.get(`${API_BASE_URL}/api/leave-transactions/summary/${employeeId}`, { params: { financialYear }, headers: { Authorization: `Bearer ${token}` } });
      setLeaveSummary(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchLeaves = async () => {
    const token = localStorage.getItem("token");
    const employeeId = localStorage.getItem("employeeId");
    if (!token || !employeeId || !financialYear) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/api/leave-transactions/employee/${employeeId}/avail`, { params: { financialYear }, headers: { Authorization: `Bearer ${token}` } });
      const mapped = res.data.map(l => ({ ...l, status: l.leaveStatus }))
        .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
      setLeaves(mapped);
      setFilteredLeaves(mapped);
    } catch (err) {
      setError(err.response?.status === 403 ? "Not authorized to view this data." : "Unable to fetch leave data.");
    }
  };

  /* ── Filters ── */
  const normalizeLeaveType = (s = "") => s.toLowerCase().replace(/\s+/g, "").replace(/[()]/g, "");

  useEffect(() => {
    let data = [...leaves];
    if (fromDate && toDate && new Date(toDate) < new Date(fromDate)) { toast.error("End date must be ≥ start date."); return; }
    if (filterType !== "ALL") data = data.filter(l => normalizeLeaveType(l.leaveType) === normalizeLeaveType(filterType));
    if (filterStatus !== "ALL") data = data.filter(l => l.status === filterStatus);
    if (fromDate) data = data.filter(l => new Date(l.startDate) >= new Date(fromDate));
    if (toDate) data = data.filter(l => new Date(l.endDate) <= new Date(toDate));
    setFilteredLeaves(data);
  }, [filterType, filterStatus, fromDate, toDate, leaves]);

  const clearFilters = () => { setFilterType("ALL"); setFilterStatus("ALL"); setFromDate(""); setToDate(""); setFilteredLeaves(leaves); };

  /* ── Sort ── */
  const handleSort = (key) => setSortConfig(p => ({ key, direction: p.key === key && p.direction === "asc" ? "desc" : "asc" }));

  const sortedLeaves = [...filteredLeaves].sort((a, b) => {
    if (!sortConfig.key) return 0;
    let aV = sortConfig.key === "startDate" ? new Date(a.startDate) : a[sortConfig.key];
    let bV = sortConfig.key === "startDate" ? new Date(b.startDate) : b[sortConfig.key];
    if (aV < bV) return sortConfig.direction === "asc" ? -1 : 1;
    if (aV > bV) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  /* ── Edit ── */
  const openEditModal = (leave) => { setEditingLeave(leave); setEditForm({ leaveType: leave.leaveType, startDate: leave.startDate, endDate: leave.endDate, reason: leave.reason, document: null }); };
  const handleEditChange = (e) => { const { name, value, files } = e.target; setEditForm(f => ({ ...f, [name]: name === "document" ? files[0] : value })); };

  const handleUpdate = async () => {
    const token = localStorage.getItem("token");
    if (!token || !editingLeave) return;
    try {
      const days = calcDays(editForm.startDate, editForm.endDate);
      const summary = leaveSummary.find(s => s.leaveType === editForm.leaveType);
      let finalType = editForm.leaveType;
      if (!summary || summary.remaining < days) { finalType = "Loss of Pay(LOP)"; toast.info("Insufficient balance — changed to LOP."); }
      await axios.put(`${API_BASE_URL}/api/leave-transactions/apply/${editingLeave.id}`, { leaveType: finalType, startDate: editForm.startDate, endDate: editForm.endDate, reason: editForm.reason, financialYear: editingLeave.financialYear }, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
      setEditingLeave(null);
      fetchLeaves(); fetchLeaveSummary();
      toast.success("Leave updated successfully!");
    } catch (err) { toast.error(err.response?.data || "Failed to update leave."); }
  };

  const calcDays = (s, e) => { if (!s || !e) return 0; return Math.round((new Date(e) - new Date(s)) / 86400000) + 1; };

  /* ── Cancel ── */
  const handleCancel = async () => {
    if (!cancelModal) return;
    if (!cancelReason.trim()) { toast.warning("Please enter a cancellation reason."); return; }
    const token = localStorage.getItem("token");
    try {
      await axios.put(`${API_BASE_URL}/api/leave-transactions/cancel/${cancelModal.id}`, { actionReason: cancelReason }, { headers: { Authorization: `Bearer ${token}` } });
      setCancelModal(null);
      fetchLeaves();
      toast.success("Leave cancelled successfully.");
    } catch { toast.error("Failed to cancel leave."); }
  };

  const sortArrow = (key) => {
    if (sortConfig.key !== key) return <span style={styles.sortIcon}>↕</span>;
    return <span style={{ ...styles.sortIcon, opacity: 1, color: "#2D3748" }}>{sortConfig.direction === "asc" ? "↑" : "↓"}</span>;
  };

  const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

  /* ─── Render ─── */
  return (
    <div style={styles.root}>
      <div className="fixed-top"><EmployeeNavbar /></div>
      <ToastContainer position="top-right" autoClose={3000} />

      <div style={styles.pageWrapper}>
        {/* Header */}
        <div style={styles.headerRow}>
          <div>
            <h2 style={styles.pageTitle}>My Leave Requests</h2>
            <p style={styles.pageSubtitle}>Track and manage your leave applications</p>
          </div>
          <button className="ml-newbtn" style={styles.newBtn} onClick={() => navigate("/leave")}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            New
          </button>
        </div>

        {error && (
          <div style={{ padding: "12px 16px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "8px", color: "#991B1B", fontSize: "0.83rem", marginBottom: "16px" }}>
            ⚠ {error}
          </div>
        )}

        {/* Filters */}
        <div style={styles.filterCard}>
          <div style={styles.filterGroup}>
            <span style={styles.filterLabel}>Leave Type</span>
            <select style={styles.filterSelect} value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="ALL">All Types</option>
              {leaveTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <span style={styles.filterLabel}>Status</span>
            <select style={styles.filterSelect} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="ALL">All Status</option>
              {["PENDING", "APPROVED", "REJECTED", "FORWARDED", "CANCELLED"].map(s => <option key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</option>)}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <span style={styles.filterLabel}>From Date</span>
            <input type="date" style={styles.filterInput} value={fromDate} onChange={e => setFromDate(e.target.value)} />
          </div>

          <div style={styles.filterGroup}>
            <span style={styles.filterLabel}>To Date</span>
            <input type="date" style={styles.filterInput} value={toDate} min={fromDate} onChange={e => setToDate(e.target.value)} />
          </div>

          <button className="ml-clearbtn" style={styles.clearBtn} onClick={clearFilters}>Clear</button>

          <div style={{ marginLeft: "auto", display: "flex", alignItems: "flex-end", gap: "8px" }}>
            <span style={{ fontSize: "0.78rem", color: "#A0AEC0" }}>
              {sortedLeaves.length} record{sortedLeaves.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Table */}
        <div style={styles.tableCard}>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead style={styles.thead}>
                <tr>
                  {[
                    { label: "Sr.no", key: null, width: "48px" },
                    { label: "Actions", key: null, width: "140px" },
                    { label: "Leave Type", key: "leaveType" },
                    { label: "Start Date", key: "startDate" },
                    { label: "End Date", key: "endDate" },
                    { label: "Days", key: null },
                    { label: "Reason", key: null },
                    { label: "Status", key: "status" },
                    { label: "Remarks", key: null },
                  ].map(({ label, key, width }) => (
                    <th key={label} style={{ ...styles.th, width }} onClick={() => key && handleSort(key)}>
                      {label}{key && sortArrow(key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedLeaves.length === 0 ? (
                  <tr>
                    <td colSpan="9">
                      <div style={styles.emptyState}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#CBD5E0" strokeWidth="1.5" style={{ marginBottom: "10px" }}>
                          <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        <p style={{ margin: 0, fontSize: "0.88rem", fontWeight: "500" }}>No leave records found</p>
                        <p style={{ margin: "4px 0 0", fontSize: "0.78rem" }}>Try adjusting your filters</p>
                      </div>
                    </td>
                  </tr>
                ) : sortedLeaves.map((leave, idx) => (
                  <tr key={leave.id} className="ml-tr">
                    <td style={{ ...styles.td, color: "#A0AEC0", fontWeight: "500", width: "48px" }}>{idx + 1}</td>

                    <td style={{ ...styles.td, width: "110px" }}>
                      {leave.status === "PENDING" ? (
                        <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                          <span className="ml-action-edit" style={styles.actionLink("#3B82F6")} onClick={() => openEditModal(leave)}>Edit</span>
                          {new Date(leave.startDate) > new Date() ? (
                            <span className="ml-action-cancel" style={styles.actionLink("#EF4444")} onClick={() => { setCancelModal(leave); setCancelReason(""); }}>Cancel</span>
                          ) : (
                            <span style={styles.actionLinkDisabled}>Cancel</span>
                          )}
                        </div>
                      ) : <span style={{ color: "#E2E8F0", fontSize: "0.75rem" }}>—</span>}
                    </td>

                    <td style={{ ...styles.td, fontWeight: "500" }}>{leave.leaveType}</td>
                    <td style={styles.td}>{fmtDate(leave.startDate)}</td>
                    <td style={styles.td}>{fmtDate(leave.endDate)}</td>
                    <td style={{ ...styles.td, textAlign: "center" }}>
                      <span style={{ display: "inline-block", padding: "2px 10px", background: "#F7F8FA", border: "1px solid #E2E8F0", borderRadius: "12px", fontWeight: "600", fontSize: "0.78rem", color: "#4A5568" }}>
                        {Math.max(0, calcDays(leave.startDate, leave.endDate))}d
                      </span>
                    </td>
                    <td
                      title={leave.reason}
                      style={{
                        ...styles.td,
                        maxWidth: "200px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "#4A5568",
                        cursor: "pointer"
                      }}
                    >
                      {leave.reason}
                    </td>

                    <td style={styles.td}>
                      <span style={styles.badge(leave.status)}>
                        <span style={styles.dot(leave.status)} />
                        {leave.status?.charAt(0) + leave.status?.slice(1).toLowerCase()}
                      </span>
                    </td>

                    <td
                      title={leave.rejectionReason || leave.cancelReason || leave.actionReason}
                      style={{
                        ...styles.td,
                        color: "#718096",
                        fontSize: "0.8rem",
                        maxWidth: "180px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        cursor: "pointer"
                      }}
                    >
                      {leave.rejectionReason || leave.cancelReason || leave.actionReason || <span style={{ color: "#E2E8F0" }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Edit Modal ── */}
      {editingLeave && (
        <div style={styles.backdrop} onClick={e => e.target === e.currentTarget && setEditingLeave(null)}>
          <div style={styles.modalBox}>
            <div style={styles.modalHeader}>
              <h5 style={styles.modalTitle}>Edit Leave Request</h5>
              <button style={styles.closeBtn} onClick={() => setEditingLeave(null)}>✕</button>
            </div>
            <div style={styles.modalBody}>
              {/* Two‑column grid for the main fields */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                {/* Leave Type */}
                <div>
                  <label style={styles.formLabel}>Leave Type</label>
                  <select
                    name="leaveType"
                    value={editForm.leaveType}
                    onChange={handleEditChange}
                    style={styles.formControl}
                  >
                    <option>Select</option>
                    {leaveTypes.map(t => (
                      <option key={t.id} value={t.name}>{t.name}</option>
                    ))}
                  </select>
                </div>

                {/* Start Date */}
                <div>
                  <label style={styles.formLabel}>Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={editForm.startDate}
                    onChange={handleEditChange}
                    style={styles.formControl}
                  />
                </div>

                {/* End Date */}
                <div>
                  <label style={styles.formLabel}>End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={editForm.endDate}
                    onChange={handleEditChange}
                    style={styles.formControl}
                  />
                </div>

                {/* Optional: leave a cell empty for now, or add future field */}
                <div></div>
              </div>

              {/* Reason – spans both columns */}
              <div style={{ marginBottom: "14px", gridColumn: "span 2" }}>
                <label style={styles.formLabel}>Reason</label>
                <textarea
                  name="reason"
                  value={editForm.reason}
                  onChange={handleEditChange}
                  rows={3}
                  style={{ ...styles.formControl, resize: "vertical" }}
                />
              </div>

              {/* Supportive Document – only for SICK leave, spans both columns */}
              {editForm.leaveType === "SICK" && (
                <div style={{ marginBottom: "14px", gridColumn: "span 2" }}>
                  <label style={styles.formLabel}>Supportive Document</label>
                  <input
                    type="file"
                    name="document"
                    onChange={handleEditChange}
                    style={styles.formControl}
                  />
                  {editingLeave.supportiveDocument && (
                    <div style={{ marginTop: "5px", fontSize: "0.78rem", color: "#718096" }}>
                      Current:&nbsp;
                      <a
                        href={`${API_BASE_URL}/uploads/${editingLeave.supportiveDocument}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#3B82F6" }}
                      >
                        {editingLeave.supportiveDocument}
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Day preview – spans both columns */}
              {editForm.startDate && editForm.endDate && (
                <div style={{ padding: "10px 14px", background: "#F7F9FC", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "0.8rem", color: "#4A5568", gridColumn: "span 2" }}>
                  📅 <strong>{calcDays(editForm.startDate, editForm.endDate)}</strong> day(s) selected
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button style={styles.btnPrimary} onClick={handleUpdate}>Save Changes</button>
              <button style={styles.btnSecondary} onClick={() => setEditingLeave(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Cancel Modal ── */}
      {cancelModal && (
        <div style={styles.backdrop} onClick={e => e.target === e.currentTarget && setCancelModal(null)}>
          <div style={styles.modalBox}>
            <div style={{ ...styles.modalHeader, borderBottom: "1px solid #F0F2F5" }}>
              <h5 style={styles.modalTitle}>Cancel Leave Request</h5>
              <button style={styles.closeBtn} onClick={() => setCancelModal(null)}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <div style={{ display: "flex", gap: "12px", padding: "12px 14px", background: "#FFF7ED", border: "1px solid #FDE68A", borderRadius: "9px", marginBottom: "16px", alignItems: "flex-start" }}>
                <span style={{ fontSize: "1.1rem" }}>⚠️</span>
                <div>
                  <p style={{ margin: 0, fontWeight: "600", fontSize: "0.85rem", color: "#92400E" }}>Confirm Cancellation</p>
                  <p style={{ margin: "2px 0 0", fontSize: "0.8rem", color: "#B45309" }}>
                    You are about to cancel your <strong>{cancelModal.leaveType}</strong> leave.
                  </p>
                </div>
              </div>
              <div>
                <label style={styles.formLabel}>Reason for Cancellation</label>
                <textarea
                  className="form-control"
                  value={cancelReason}
                  onChange={e => setCancelReason(e.target.value)}
                  rows={3}
                  placeholder="Please explain why you're cancelling this request..."
                  style={{ ...styles.formControl, resize: "vertical" }}
                />
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.btnDanger} onClick={handleCancel}>Confirm Cancel</button>
              <button style={styles.btnSecondary} onClick={() => setCancelModal(null)}>Go Back</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyLeaves;